<template>
  <b-modal
    :id="modalId"
    v-model="isModalVisible"
    size="xl"
    scrollable
    hide-footer
    no-close-on-backdrop
    title="Editar empleado"
    @hidden="handleModalHidden"
  >
    <div class="employee-form-panel">
      <h4 class="mb-1">Empleado seleccionado</h4>
      <b-table
        class="mb-2"
        :items="[employee]"
        :fields="singleEmployeeColumns"
        responsive
        bordered
      >
        <template #cell(name)="data">
          {{ data.item.name }} {{ data.item.first_name }} {{ data.item.second_name }}
        </template>
      </b-table>

      <b-form class="mt-1" @submit.prevent="saveEmployee">
        <h4 class="mb-1">Información adicional de empleado</h4>
        <b-row class="mb-2">
          <b-col cols="6">
            <b-form-group
              label="Municipio"
              label-class="h6 font-weight-normal"
              :label-for="`emp-municipality-${branch.id}`"
            >
              <b-form-input
                :id="`emp-municipality-${branch.id}`"
                v-model="employee.municipality"
                :disabled="isReadOnly"
              />
            </b-form-group>
          </b-col>

          <b-col cols="6">
            <b-form-group
              label="Colonia"
              label-class="h6 font-weight-normal"
              :label-for="`emp-district-${branch.id}`"
            >
              <b-form-input
                :id="`emp-district-${branch.id}`"
                v-model="employee.district"
                :disabled="isReadOnly"
              />
            </b-form-group>
          </b-col>

          <b-col cols="12">
            <b-form-group
              label="Dirección"
              label-class="h6 font-weight-normal"
              :label-for="`emp-address-${branch.id}`"
            >
              <b-form-input
                :id="`emp-address-${branch.id}`"
                v-model="employee.address"
                :disabled="isReadOnly"
              />
            </b-form-group>
          </b-col>

          <b-col cols="6">
            <b-form-group
              label="Posición"
              label-class="h6 font-weight-normal"
              :label-for="`emp-position-${branch.id}`"
            >
              <b-form-input
                :id="`emp-position-${branch.id}`"
                v-model="employee.position"
                :disabled="isReadOnly"
              />
            </b-form-group>
          </b-col>

          <b-col cols="6">
            <b-form-group
              label="Teléfono de emergencias"
              label-class="h6 font-weight-normal"
              :label-for="`emp-emergency-phone-${branch.id}`"
            >
              <b-form-input
                :id="`emp-emergency-phone-${branch.id}`"
                v-model="employee.emergency_phone"
                :disabled="isReadOnly"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <h4 class="mb-1">Información de crisis</h4>
        <b-row>
          <b-col cols="6">
            <b-form-group
              label="Estatus por respuesta de calltree"
              label-class="h6 font-weight-normal"
              :label-for="`emp-calltree-${branch.id}`"
            >
              <v-select
                :id="`emp-calltree-${branch.id}`"
                v-model="employee.call_tree_status"
                :options="callTreeStatusOptions"
                placeholder="Seleccione estatus"
                label="label"
                :reduce="(val) => val.value"
                :disabled="isReadOnly"
                @input="checkCallTreeOption"
              />
              <span v-if="errors.call_tree_status" class="text-danger">
                {{ errors.call_tree_status }}
              </span>
            </b-form-group>
          </b-col>

          <b-col cols="6">
            <b-form-group
              label="Contactado por:"
              label-class="h6 font-weight-normal"
              :label-for="`emp-contacted-${branch.id}`"
            >
              <v-select
                :id="`emp-contacted-${branch.id}`"
                v-model="employee.contacted_by"
                :options="contactedByOptions"
                placeholder="Seleccione"
                label="label"
                :reduce="(val) => val.value"
                :disabled="isReadOnly"
              />
              <span v-if="errors.contacted_by" class="text-danger">
                {{ errors.contacted_by }}
              </span>
            </b-form-group>
          </b-col>

          <b-col v-if="showEvacuationOptions" cols="6">
            <b-form-group
              label="Traslado"
              label-class="h6 font-weight-normal"
              :label-for="`evacuation-route-${branch.id}`"
            >
              <v-select
                :id="`evacuation-route-${branch.id}`"
                v-model="employee.evacuation_route"
                :options="evacuationRouteOptions"
                placeholder="Seleccione opción"
                label="label"
                :reduce="(val) => val.value"
                :disabled="isReadOnly"
              />
              <span v-if="errors.evacuation_route" class="text-danger">
                {{ errors.evacuation_route }}
              </span>
            </b-form-group>
          </b-col>

          <b-col v-if="showShelterOption" cols="6">
            <b-form-group
              label="Albergue:"
              label-class="h6 font-weight-normal"
              :label-for="`emp-shelter-${branch.id}`"
            >
              <v-select
                :input-id="`emp-shelter-${branch.id}`"
                v-model="employee.id_shelter"
                :options="shelters"
                placeholder="Seleccione albergue"
                label="name"
                :reduce="(val) => val.id"
                :disabled="isReadOnly"
              />
              <span v-if="errors.id_shelter" class="text-danger">
                {{ errors.id_shelter }}
              </span>
            </b-form-group>
          </b-col>

          <b-col cols="6">
            <b-form-group
              label="Asignación laboral"
              label-class="h6 font-weight-normal"
              :label-for="`emp-assigment-${branch.id}`"
            >
              <v-select
                :id="`emp-assigment-${branch.id}`"
                v-model="employee.job_assignment"
                :options="jobAssigmentOptions"
                placeholder="Seleccionar"
                label="label"
                :reduce="(val) => val.value"
                :disabled="isReadOnly"
              />
              <span v-if="errors.job_assignment" class="text-danger">
                {{ errors.job_assignment }}
              </span>
            </b-form-group>
          </b-col>

          <b-col v-if="showShelterOption && shelterSelected" cols="12">
            <p class="font-weight-bold h6 mb-1">
              <strong>Información de albergue</strong>
            </p>
            <b-table
              class="mb-2"
              :items="[shelterSelected]"
              :fields="shelterColumns"
              responsive
              bordered
            />
          </b-col>

          <b-col cols="12">
            <p class="font-weight-bold h6 mb-1">
              <strong>Daños</strong>
            </p>
            <b-row>
              <b-col
                v-for="(aur, dIdx) in damageOptions"
                :key="dIdx"
                class="mb-1"
                cols="4"
              >
                <b-form-checkbox
                  v-model="employee.affects"
                  :name="`damage-option-${dIdx}`"
                  :value="aur.id"
                  :disabled="isReadOnly"
                >
                  {{ aur.name }}
                </b-form-checkbox>
              </b-col>
            </b-row>
          </b-col>

          <b-col cols="12">
            <p class="font-weight-bold h6 mb-1">
              <strong>Necesidades</strong>
            </p>
            <b-row>
              <b-col
                v-for="(aur, dIdx) in needOptions"
                :key="dIdx"
                class="mb-1"
                cols="4"
              >
                <b-form-checkbox
                  v-model="employee.affects"
                  :name="`need-option-${dIdx}`"
                  :value="aur.id"
                  :disabled="isReadOnly"
                >
                  {{ aur.name }}
                </b-form-checkbox>
              </b-col>
              <b-col v-if="showOtherOptionText" cols="12">
                <b-form-group
                  label="Ingresar otro"
                  :label-for="`other-option-${branch.id}`"
                >
                  <b-form-input
                    :id="`other-option-${branch.id}`"
                    v-model="employee.other_option"
                    :disabled="isReadOnly"
                  />
                </b-form-group>
              </b-col>
            </b-row>
          </b-col>

          <b-col v-if="showDeliveredOptions" cols="12">
            <p class="font-weight-bold h6 mb-1">
              <strong>Entregado</strong>
            </p>
            <b-row>
              <b-col
                v-for="(aur, dIdx) in deliveredOptions"
                :key="dIdx"
                class="mb-1"
                cols="4"
              >
                <b-form-checkbox
                  v-model="employee.affects"
                  :name="`delivered-option-${dIdx}`"
                  :value="aur.id"
                  :disabled="isReadOnly"
                >
                  Si
                </b-form-checkbox>
              </b-col>
            </b-row>
          </b-col>
        </b-row>

        <div class="d-flex justify-content-end">
          <b-button
            type="button"
            variant="danger"
            class="mr-1"
            :disabled="loading"
            @click="closeFormEmployee"
          >
            Cancelar
          </b-button>
          <b-button
            v-if="showOptionsByStatus && !isReadOnly"
            type="submit"
            variant="primary"
            :disabled="loading"
          >
            Guardar
          </b-button>
        </div>
      </b-form>
    </div>
  </b-modal>
</template>

<script>
import {computed, ref, watch} from '@vue/composition-api';
import axios from "@/libs/axios";
import {useToast} from "vue-toastification/composition";
import ToastificationContent
  from "@core/components/toastification/ToastificationContent.vue";
import vSelect from "vue-select";
import {useFullscreenModalTransport} from './composables/useFullscreenModalTransport';

export default {
  name: "EmployeeEditModal",
  components: {
    ToastificationContent,
    vSelect
  },
  props: {
    affectsUsersRh: {
      type: Object,
      default: () => ({})
    },
    branch: {
      type: Object,
      required: true,
    },
    employeeEdit: {
      type: Object,
      default: null,
    },
    sheltersByMunicipality: {
      type: Object,
      default: () => ({})
    },
    operationalSecurityCrisis: {
      type: Object,
      required: true,
    },
    showModal: {
      type: Boolean,
      default: false,
    },
    showOptionsByStatus: {
      type: Boolean
    },
    isReadOnly: {
      type: Boolean,
      default: false
    },
  },
  setup(props, {emit}) {
    const toast = useToast();
    const modalId = computed(() => `crisis-employee-edit-${props.branch?.id || props.branch?.id_unit || 'modal'}`);

    const callTreeStatusOptions = ref([
      {value: 'fine', label: 'Me encuentro bien'},
      {value: 'injured', label: 'Necesito ayuda'},
      {value: 'unreachable', label: 'No localizable'},
      {value: 'evacuated', label: 'Evacuado'},
      {value: 'by_mistake', label: 'Por error'},
    ]);

    const contactedByOptions = ref([
      {value: 'ccb', label: 'CCB'},
      {value: 'business', label: 'Negocio'},
      {value: 'csis', label: 'CSIS'},
    ]);

    const evacuationRouteOptions = ref([
      {value: 'family_home', label: 'Domicilio familiar'},
      {value: 'shelter', label: 'Albergue'}
    ]);

    const jobAssigmentOptions = ref([
      {value: 'cpae', label: 'CPAE'},
      {value: 'branch', label: 'Sucursal'},
      {value: 'business_partners', label: 'Socios comerciales'},
      {value: 'building', label: 'Edificio'},
      {value: 'afore', label: 'Afore'},
      {value: 'pymes', label: 'PyMES'},
      {value: 'sec', label: 'SEC'},
      {value: 'patrimonial', label: 'Patrimonial'},
      {value: 'travelers', label: 'Viajeros'}
    ]);

    const shelterColumns = [
      {key: 'name', label: 'Nombre'},
      {key: 'address', label: 'Direccíon'},
      {key: 'phone_1', label: 'Teléfono 1'},
      {key: 'phone_2', label: 'Teléfono 2'}
    ];

    const singleEmployeeColumns = [
      {key: 'soeid', label: 'SOEID'},
      {key: 'name', label: 'Nombre'},
      {key: 'email', label: 'Email'}
    ];

    const createEmptyEmployee = () => ({
      id: null,
      name: null,
      first_name: null,
      second_name: null,
      soeid: null,
      email: null,
      contact_phone: null,
      emergency_phone: null,
      call_tree_status: null,
      contacted_by: null,
      evacuation_route: null,
      affects: [],
      other_option: null,
      id_shelter: null,
      job_assignment: null
    });

    const createEmptyErrors = () => ({
      call_tree_status: null,
      contacted_by: null
    });

    const employee = ref(createEmptyEmployee());
    const errors = ref(createEmptyErrors());
    const loading = ref(false);
    const showEvacuationOptions = ref(false);

    const isModalVisible = computed({
      get: () => props.showModal,
      set: (value) => {
        if (!value) {
          closeFormEmployee();
        }
      }
    });

    useFullscreenModalTransport(modalId, isModalVisible);

    const damageOptions = computed(() => props.affectsUsersRh.damages || []);
    const deliveredOptions = computed(() => props.affectsUsersRh.delivered || []);
    const needOptions = computed(() => props.affectsUsersRh.needs || []);
    const shelters = computed(() => props.sheltersByMunicipality?.[props.branch?.municipality?.id] || []);

    const shelterSelected = computed(() => (
      shelters.value.find(shelter => employee.value.id_shelter === shelter.id)
    ));

    const showDeliveredOptions = computed(() => {
      const optionsDamages = [3, 6, 7];

      return optionsDamages.some(damageOption => employee.value.affects.includes(damageOption));
    });

    const showShelterOption = computed(() => employee.value.evacuation_route === 'shelter');
    const showOtherOptionText = computed(() => employee.value.affects.includes(7));

    const checkCallTreeOption = (value) => {
      showEvacuationOptions.value = (value === 'evacuated');

      if (!showEvacuationOptions.value) {
        employee.value.evacuation_route = null;
        employee.value.id_shelter = null;
      }
    }

    const closeFormEmployee = () => {
      emit('update:isActive', false);
    }

    const findUserRh = (userRhBySearch) => {
      const unitsUsersRh = props.operationalSecurityCrisis?.units_users_rh || [];
      const idUserRh = userRhBySearch?.id_user_rh || userRhBySearch?.user_rh?.id || userRhBySearch?.id;
      const idUnit = props.branch?.id_unit;

      return unitsUsersRh.find(
        userRh => String(userRh.id_user_rh) === String(idUserRh) && String(userRh.id_unit) === String(idUnit)
      );
    }

    const handleModalHidden = () => {
      resetFormEmployee();
    }

    const openFormEmployee = (employeeEdit) => {
      resetFormEmployee();

      const userRh = findUserRh(employeeEdit);
      const otherOption = (userRh && Array.isArray(userRh.affects))
        ? userRh.affects.find(affect => affect.id === 7)
        : null;
      const additionalInfo = employeeEdit.user_rh?.additional_info || {};
      const idShelter = employeeEdit.shelter?.id_operational_security_crisis_shelter || null;

      employee.value = {
        id: employeeEdit.user_rh?.id || employeeEdit.id,
        name: employeeEdit.user_rh?.name,
        first_name: employeeEdit.user_rh?.first_name,
        second_name: employeeEdit.user_rh?.second_name,
        soeid: employeeEdit.user_rh?.soeid,
        email: employeeEdit.user_rh?.email,
        position: additionalInfo.position || null,
        phone: additionalInfo.phone || null,
        emergency_phone: additionalInfo.emergency_phone || null,
        address: additionalInfo.address || null,
        district: additionalInfo.district || null,
        municipality: additionalInfo.municipality || null,
        call_tree_status: userRh?.call_tree_status || null,
        contacted_by: userRh?.contacted_by || null,
        evacuation_route: userRh?.evacuation_route || null,
        affects: (userRh && Array.isArray(userRh.affects)) ? userRh.affects.map(a => a.id) : [],
        other_option: otherOption ? otherOption.pivot?.value : null,
        id_shelter: idShelter,
        job_assignment: employeeEdit?.job_assignment
      }

      checkCallTreeOption(employee.value.call_tree_status);
    }

    const resetFormEmployee = () => {
      showEvacuationOptions.value = false;
      employee.value = createEmptyEmployee();
      errors.value = createEmptyErrors();
    }

    const saveEmployee = () => {
      if (props.isReadOnly) {
        return;
      }

      const hasErrors = validateData();

      if (hasErrors) {
        return;
      }

      loading.value = true;

      const payload = {
        id_operational_security_crisis: props.operationalSecurityCrisis.id,
        call_tree_status: employee.value.call_tree_status,
        contacted_by: employee.value.contacted_by,
        other_option: employee.value.other_option,
        evacuation_route: employee.value.evacuation_route,
        id_shelter: employee.value.id_shelter,
        job_assignment: employee.value.job_assignment,
        users: [{
          ...employee.value,
          id_unit: props.branch.id_unit,
          id_user_rh: employee.value.id,
          affects: employee.value.affects
        }]
      }

      axios.post('/v1/operational-security-crisis/users-rh', payload)
        .then(response => {
          const {data} = response;
          const affectsWithLackOfPublicTransportation = employee.value.affects.includes(5);

          emit('save-employee-success', data.data);

          toast({
            component: ToastificationContent,
            props: {
              title: data.message,
              icon: 'CheckIcon',
              variant: 'success',
            },
          });

          emit('save-affectation', props.branch, affectsWithLackOfPublicTransportation);
          closeFormEmployee();
        })
        .catch(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Error',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          });
        })
        .finally(() => {
          loading.value = false;
        });
    }

    const validateData = () => {
      let hasErrors = false;

      errors.value = createEmptyErrors();

      if (!employee.value.call_tree_status) {
        errors.value.call_tree_status = 'Campo requerido';
        hasErrors = true;
      }

      if (!employee.value.contacted_by) {
        errors.value.contacted_by = 'Campo requerido'
        hasErrors = true;
      }

      return hasErrors;
    }

    watch(
      () => props.showModal,
      (visible) => {
        if (visible && props.employeeEdit) {
          openFormEmployee(props.employeeEdit);
        }
      },
      {immediate: true}
    );

    watch(
      () => props.employeeEdit,
      (employeeEdit) => {
        if (props.showModal && employeeEdit) {
          openFormEmployee(employeeEdit);
        }
      }
    );

    return {
      callTreeStatusOptions,
      contactedByOptions,
      damageOptions,
      deliveredOptions,
      employee,
      errors,
      evacuationRouteOptions,
      isModalVisible,
      jobAssigmentOptions,
      loading,
      modalId,
      needOptions,
      shelterColumns,
      showEvacuationOptions,
      singleEmployeeColumns,

      shelters,
      shelterSelected,
      showDeliveredOptions,
      showShelterOption,
      showOtherOptionText,

      checkCallTreeOption,
      closeFormEmployee,
      handleModalHidden,
      saveEmployee,
    }
  },
}
</script>

<style lang="scss" scoped>
.employee-form-panel {
  width: 100%;
}
</style>
